import React, { useState, useEffect, useCallback } from "react";
import "./CaixaHolo.css";
import { API_ENDPOINTS } from "../config/api";
import { getVideoUrl } from "../config/assets";
import { getConfigSync } from "../config/runtimeConfig";

interface RegistrationData {
  id: string;
  external_id: string;
  name: string;
  phrase: string;
  phrase2: string;
  speciality: string;
  type_code: string;
  photo_url: string;
  video_url: string | null;
}

interface CaixaUserResponse {
  success: boolean;
  message: string;
  data: {
    temp_data_id: string;
    registration: RegistrationData;
    checkin_time: string;
  } | null;
}

const CaixaHolo: React.FC = () => {
  const [currentUser, setCurrentUser] = useState<RegistrationData | null>(null);
  const [isPolling, setIsPolling] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isPlayingVideo, setIsPlayingVideo] = useState(false);
  const [isLoadingVideo, setIsLoadingVideo] = useState(false);
  const [videoReady, setVideoReady] = useState(false);

  // Nome do cache
  const CACHE_NAME = 'caixa-holo-videos';

  // Função para verificar se vídeo está em cache
  const isVideoInCache = useCallback(async (videoUrl: string): Promise<boolean> => {
    try {
      const cache = await caches.open(CACHE_NAME);
      const response = await cache.match(videoUrl);
      return !!response;
    } catch (error) {
      console.log('Erro ao verificar cache:', error);
      return false;
    }
  }, []);

  // Função para adicionar vídeo ao cache
  const addVideoToCache = useCallback(async (videoUrl: string): Promise<void> => {
    try {
      const cache = await caches.open(CACHE_NAME);
      await cache.add(videoUrl);
    } catch (error) {
      console.log('Erro ao adicionar ao cache:', error);
    }
  }, []);

  // Função para buscar vídeo (cache first)
  const fetchVideoFromCacheOrNetwork = useCallback(async (videoUrl: string): Promise<string> => {
    try {
      // 1. Verificar se está em cache
      const cache = await caches.open(CACHE_NAME);
      const cachedResponse = await cache.match(videoUrl);
      
      if (cachedResponse) {
        console.log('🎯 Vídeo carregado do cache:', videoUrl);
        // Retornar blob URL do cache
        const blob = await cachedResponse.blob();
        return URL.createObjectURL(blob);
      }
      
      // 2. Se não está em cache, baixar da rede
      console.log('🌐 Baixando vídeo da rede:', videoUrl);
      const response = await fetch(videoUrl);
      
      if (!response.ok) {
        throw new Error(`Erro HTTP: ${response.status}`);
      }
      
      // 3. Clonar resposta para cache e para uso
      const responseClone = response.clone();
      
      // 4. Adicionar ao cache
      await cache.put(videoUrl, responseClone);
      
      // 5. Retornar blob URL
      const blob = await response.blob();
      return URL.createObjectURL(blob);
      
    } catch (error) {
      console.error('Erro ao buscar vídeo:', error);
      throw error;
    }
  }, []);

  // Função para fazer fetch silencioso (sem logs de erro no console)
  const silentFetch = useCallback(async (url: string, options: RequestInit = {}) => {
    const originalError = console.error;
    const originalWarn = console.warn;
    
    try {
      // Temporariamente suprimir logs de erro
      console.error = () => {};
      console.warn = () => {};
      
      // Obter token de autenticação
      const config = getConfigSync();
      const authHeaders: Record<string, string> = {};
      if (config.apiToken) {
        authHeaders['Authorization'] = `Token ${config.apiToken}`;
      }
      
      // Configurações padrão para CORS
      const defaultOptions: RequestInit = {
        mode: 'cors',
        credentials: 'omit', // Não enviar cookies para evitar problemas de CORS
        headers: {
          'Content-Type': 'application/json',
          ...authHeaders,
          ...options.headers,
        },
        ...options,
      };
      
      const response = await fetch(url, defaultOptions);
      return response;
    } finally {
      // Restaurar logs
      console.error = originalError;
      console.warn = originalWarn;
    }
  }, []);

  // Função para preload do vídeo com cache
  const preloadVideo = useCallback(async (videoUrl: string): Promise<string> => {
    try {
      // Buscar vídeo do cache ou rede
      const blobUrl = await fetchVideoFromCacheOrNetwork(videoUrl);
      
      // Aguardar o vídeo estar pronto para reprodução
      return new Promise((resolve, reject) => {
        const video = document.createElement('video');
        
        const handleCanPlay = () => {
          video.removeEventListener('canplay', handleCanPlay);
          video.removeEventListener('error', handleError);
          resolve(blobUrl);
        };
        
        const handleError = () => {
          video.removeEventListener('canplay', handleCanPlay);
          video.removeEventListener('error', handleError);
          URL.revokeObjectURL(blobUrl); // Limpar blob URL em caso de erro
          reject(new Error('Erro ao carregar vídeo'));
        };
        
        video.addEventListener('canplay', handleCanPlay);
        video.addEventListener('error', handleError);
        
        // Configurações básicas
        video.preload = 'metadata';
        video.src = blobUrl;
        
        // Timeout de 30 segundos
        setTimeout(() => {
          video.removeEventListener('canplay', handleCanPlay);
          video.removeEventListener('error', handleError);
          URL.revokeObjectURL(blobUrl);
          reject(new Error('Timeout: Vídeo não foi carregado em 30 segundos'));
        }, 30000);
      });
    } catch (error) {
      console.error('Erro no preload do vídeo:', error);
      throw error;
    }
  }, [fetchVideoFromCacheOrNetwork]);

  const fetchNextUser = useCallback(async () => {
    try {
      const response = await silentFetch(API_ENDPOINTS.API_CAIXAUSER, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (response.status === 200) {
        const data: CaixaUserResponse = await response.json();
        if (data.success && data.data) {
          setCurrentUser(data.data.registration);
          setIsPolling(false);
          setError(null);
          
          // Se há vídeo, pausar polling e iniciar carregamento
          if (data.data.registration.video_url) {
            setIsLoadingVideo(true);
            setVideoReady(false);
            
            try {
              // Verificar cache primeiro
              const isInCache = await isVideoInCache(data.data.registration.video_url);
              console.log(isInCache ? '✅ Vídeo encontrado no cache' : '📥 Vídeo não está em cache, baixando...');
              
              const blobUrl = await preloadVideo(data.data.registration.video_url);
              
              // Atualizar usuário com blob URL
              setCurrentUser(prev => prev ? { ...prev, video_url: blobUrl } : null);
              setVideoReady(true);
              setIsPlayingVideo(true);
            } catch (err) {
              setError('Erro ao carregar vídeo');
              // Continuar sem vídeo
              setVideoReady(false);
              setIsPlayingVideo(false);
            } finally {
              setIsLoadingVideo(false);
            }
          } else {
            // TESTE: Se não há vídeo, usar um vídeo de teste
            const testVideoUrl = getVideoUrl('animated_wave_background_loop.mp4');
            setIsLoadingVideo(true);
            setVideoReady(false);
            
            try {
              const blobUrl = await preloadVideo(testVideoUrl);
              setVideoReady(true);
              setIsPlayingVideo(true);
              // Simular dados do usuário para teste
              if (!currentUser) {
                setCurrentUser({
                  id: 'test-id',
                  external_id: 'TEST001',
                  name: 'Usuário de Teste',
                  phrase: 'Testando sistema de vídeos',
                  phrase2: 'Sistema simplificado',
                  speciality: 'teste',
                  type_code: 'TEST',
                  photo_url: '',
                  video_url: blobUrl
                });
              } else {
                // Atualizar usuário atual com vídeo de teste
                setCurrentUser({
                  ...data.data.registration,
                  video_url: blobUrl
                });
              }
            } catch (err) {
              setError('Erro ao carregar vídeo de teste');
              setVideoReady(false);
              setIsPlayingVideo(false);
            } finally {
              setIsLoadingVideo(false);
            }
          }
        }
      } else if (response.status === 404) {
        // Nenhum usuário encontrado, continuar polling (comportamento normal)
        setError(null);
      } else {
        // Outros erros HTTP, mas continuar polling
        setError(`Erro ${response.status}`);
      }
    } catch (err) {
      // Erro de rede/conexão - silencioso, apenas definir erro na UI se necessário
      // Para polling, erros de rede são normais e não devem aparecer no console
      if (isPolling) {
        // Durante polling, não mostrar erros de conexão na UI
        setError(null);
      } else {
        setError('Erro de conexão');
      }
    }
  }, [silentFetch, isPolling, isVideoInCache, preloadVideo]);

  useEffect(() => {
    let intervalId: NodeJS.Timeout;

    if (isPolling) {
      // Fazer primeira busca imediatamente
      fetchNextUser();
      
      // Configurar polling a cada 1 segundo
      intervalId = setInterval(fetchNextUser, 1000);
    }

    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [isPolling, fetchNextUser]);

  const handleVideoEnd = () => {
    // Limpar blob URL se foi criada
    if (currentUser?.video_url && currentUser.video_url.startsWith('blob:')) {
      URL.revokeObjectURL(currentUser.video_url);
    }
    
    setIsPlayingVideo(false);
    setVideoReady(false);
    setCurrentUser(null);
    setIsPolling(true);
    setError(null);
  };

  const handleOkClick = () => {
    // Limpar blob URL se foi criada
    if (currentUser?.video_url && currentUser.video_url.startsWith('blob:')) {
      URL.revokeObjectURL(currentUser.video_url);
    }
    
    setVideoReady(false);
    setCurrentUser(null);
    setIsPolling(true);
    setError(null);
  };

  // ...existing code... (resto do JSX permanece igual)
  return (
    <div className={`caixa-holo-container ${isPlayingVideo ? 'video-mode' : ''}`}>
      <div className="caixa-holo-content">
        <div className={`caixa-holo-workspace ${isPlayingVideo ? 'video-fullscreen' : ''}`}>
          <div className="workspace-content">
            {isLoadingVideo ? (
              <div className="video-loading-state">
                <div className="loading-spinner"></div>
                <p>Carregando vídeo...</p>
                <p style={{ fontSize: '14px', opacity: 0.7 }}>
                  {currentUser?.video_url && currentUser.video_url.includes('s3') ? 
                    'Verificando cache...' : 'Aguarde o download completo'
                  }
                </p>
              </div>
            ) : isPlayingVideo && currentUser?.video_url && videoReady ? (
              <video
                key={currentUser.id}
                src={currentUser.video_url}
                autoPlay
                playsInline
                preload="metadata"
                onEnded={handleVideoEnd}
                onError={(e) => {
                  handleVideoEnd();
                }}
                onLoadedData={() => {}}
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  border: 'none',
                  outline: 'none',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0
                }}
              />
            ) : currentUser ? (
              <div className="user-info">
                <div className="user-photo">
                  {currentUser.photo_url ? (
                    <img 
                      src={currentUser.photo_url} 
                      alt={currentUser.name || 'Usuário'} 
                      className="photo"
                    />
                  ) : (
                    <div className="no-photo">Sem foto</div>
                  )}
                </div>
                
                <div className="user-details">
                  <h2>{currentUser.name || 'Nome não informado'}</h2>
                  <p><strong>ID:</strong> {currentUser.external_id}</p>
                  <p><strong>Especialidade:</strong> {currentUser.speciality || 'Não informado'}</p>
                  <p><strong>Tipo:</strong> {currentUser.type_code || 'Não informado'}</p>
                  
                  {currentUser.phrase && (
                    <div className="phrase">
                      <strong>Frase 1:</strong> {currentUser.phrase}
                    </div>
                  )}
                  
                  {currentUser.phrase2 && (
                    <div className="phrase">
                      <strong>Frase 2:</strong> {currentUser.phrase2}
                    </div>
                  )}
                </div>
                
                <button className="ok-button" onClick={handleOkClick}>
                  OK
                </button>
              </div>
            ) : (
              <video
                src={getVideoUrl('Telao_render_sombra_V2.mp4')}
                autoPlay
                muted
                loop
                playsInline
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  border: 'none',
                  outline: 'none',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  zIndex: 1
                }}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CaixaHolo; 